// src/components/MultipleSearchFilter.js
import { useState, useRef, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DataContext } from "../utils/DataContext";
import { req_Status } from "../utils/enums";
import filterIcon from "../assets/filter-icon.png";
import CustomInput from "./CustomInput";
import Dropdown from "./Dropdown";
import CustomButton from "./CustomButton";
import FilterButton from "./FilterButton";


const MultipleSearchFilter = ({
  options,
  selected,
  setSelected,
  thick,
  flex,
  errorText,
  onChange,
  nameInAr = false,
  searchQuery,
  setSearchQuery,
  className,
  setStartDate,
  setEndDate,
  startDate,
  endDate,
  handleSearch = () => {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const isFirstClick = useRef(true);
  const { t } = useTranslation("general");
  const [loading, setLoading] = useState({ status: req_Status.NULL });
  const [dateRangeError, setDateRangeError] = useState(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const toggleDropdown = async () => {
    setIsOpen(!isOpen);
  };

  const handleItemClick = (item) => {
    setSelected(item);
    if (onChange) {
      onChange(item);
    }
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      toggleDropdown();
    }
  };

  // Modified input change handler
  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    // Don't trigger getData immediately
  };

  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);

    // If end date exists, validate the range
    if (endDate && newStartDate && new Date(endDate) < new Date(newStartDate)) {
      setDateRangeError(true);
    } else {
      setDateRangeError(false);
    }
  };

  // Handle end date change with validation
  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    setEndDate(newEndDate);

    // If start date exists, validate the range
    if (startDate && newEndDate && new Date(newEndDate) < new Date(startDate)) {
      setDateRangeError(true);
    } else {
      setDateRangeError(false);
    }
  };

  const handleResetDate = (fieldName) => {
    if (fieldName === "from") {
      setStartDate("");
    } else if (fieldName === "to") {
      setEndDate("");
    }

    // Clear error if one date is removed
    if (
      (fieldName === "from" && !endDate) ||
      (fieldName === "to" && !startDate)
    ) {
      setDateRangeError(false);
    }
  };

  // Function to handle reset search that doesn't auto-trigger getData
  const handleResetSearch = () => {
    setSearchQuery("");
  };

  const showError = !selected?.value && errorText;

  return (
    <>
      <div
        className={`
      o-column o-column--vertical-align_center o-column--vertical-align_start o-column--gap-size_500 c-search-container o-column--horizontal-align_space-between ${className}
      `}
      >
        {showError && <div className="error-message">{t(errorText)}</div>}
        <section className="u-width_full-width o-column o-column--gap-size_200">
          <label className="t-bold_semi-bold">{`${t("searchFor")} ${t(
            selected?.title
          )}`}</label>
          <div className="o-row o-row--gap-size_400">
            {selected?.type === "date" ? (
              <CustomInput
                type="date"
                placeholder={`${t("searchTitle")} ${t(selected?.title)}`}
                flex
                thick
                value={searchQuery}
                onChange={handleInputChange}
                resetSearch={handleResetSearch}
                ref={inputRef}
              />
            ) : selected?.type === "dropdown" ? (
              <Dropdown
                options={selected?.dropdown?.data}
                selected={searchQuery}
                setSelected={setSearchQuery}
                thick
                flex
              />
            ) : selected?.type === "money" ? (
              <CustomInput
                type="money"
                placeholder={`${t("searchTitle")} ${t(selected?.title)}`}
                flex
                value={searchQuery}
                onChange={handleInputChange}
                resetSearch={handleResetSearch}
                ref={inputRef}
                thick
              />
            ) : selected?.type === "moneyV2" ? (
              <CustomInput
                type="moneyV2"
                placeholder={`${t("searchTitle")} ${t(selected?.title)}`}
                flex
                value={searchQuery}
                onChange={handleInputChange}
                resetSearch={handleResetSearch}
                ref={inputRef}
                thick
              />
            ) : (
              <CustomInput
                placeholder={`${t("searchTitle")} ${t(selected?.title)}`}
                flex
                value={searchQuery}
                onChange={handleInputChange}
                resetSearch={handleResetSearch}
                ref={inputRef}
                thick
              />
            )}
            <div
              className="c-search-filter"
              ref={dropdownRef}
              onClick={toggleDropdown}
              onKeyDown={handleKeyDown}
            >
              <div
                tabIndex={0}
                className={`toggle ${isOpen ? "opened" : ""} ${
                  thick ? "thick" : ""
                }`}
              >

                <FilterButton t={t} isFiltersOpen={!isOpen}/>
              </div>

              {isOpen && (
                <>
                  <ul className="filter-menu">
                    {Array.isArray(options) &&
                      options.map((option, index) => (
                        <li
                          key={index}
                          className="filter-menu-item"
                          onClick={() => handleItemClick(option)}
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              handleItemClick(option);
                            }
                          }}
                        >
                          {!nameInAr
                            ? typeof option === "object"
                              ? t(option?.title) || option?.name
                              : option
                            : typeof option === "object"
                            ? t(option?.title) || option?.nameInAr
                            : option}
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="o-row o-row--gap-size_300 multiple-search-filter o-column-row-switcher u-width_full-width">
          {/* Date Range Inputs */}
          <div className="o-row o-row--gap-size_300 multiple-search-filter-dates">
            <CustomInput
              name="from"
              type="date"
              title={t("from")}
              placeholder={`${t("searchTitle")} ${t(selected?.title)}`}
              flex
              thick
              value={startDate}
              onChange={handleStartDateChange}
              resetSearch={() => handleResetDate("from")}
              ref={inputRef}
            />
            <CustomInput
              name="to"
              type="date"
              title={t("to")}
              placeholder={`${t("searchTitle")} ${t(selected?.title)}`}
              flex
              thick
              value={endDate}
              onChange={handleEndDateChange}
              resetSearch={() => handleResetDate("to")}
              ref={inputRef}
              min={startDate}
            />
          </div>
          <CustomButton
            title={t("search")}
            onClick={handleSearch}
            icon='search'
            iconSize={15}
            classList="c-search-button"
            disabled={dateRangeError}
          />
        </section>
      </div>
    </>
  );
};

export default MultipleSearchFilter;
